import React, { useState, useEffect } from "react";
import { Box, TextField, FormControl, FormControlLabel, Checkbox, FormGroup, Autocomplete, CircularProgress, IconButton } from "@mui/material";
import { styled } from "@mui/system";
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import TestFacilityApi from "../../../../api/Admin/TestFacility";
import MethodApi from "../../../../api/LIMS/Method";
import { GlobalButton } from "../../../styles";
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { StyledTableRow, StyledTableBody, StyledTableCell, UXDataTableWithoutBody } from "../../../../components/UXDataTable";
import PiscesTest from "../../../../api/Reporting/PiscesTest";
import PiscesBacklog from "../../../../api/Reporting/PiscesBacklog";
import { convertDateFormat, formatMidasNumber, exportToExcel, DatePickerKeyDownEvent } from "../../../../global";
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FilterManagement from "../../../../components/FilterManagement";
import './styles.css';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const StyledFormControl = styled(FormControl)(() => ({
  display: 'flex',
  alignItems: 'start'
}));

const StyledBox = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '20px'
}));

const StyledShortTextField = styled(TextField)({
  id: "outlined-normal",
  width: "20rem",
  marginRight: "15px"
});

const defaultSearchFields = {
  startDate: null,
  endDate: null,
  tests: false,
  backlogs: false,
  facility: null,
  facilities: [],
  method: null,
  methods: []
}

const StyledAutocomplete = styled(Autocomplete)(() => ({
  width: "20rem",
  marginRight: "15px"
}));

const colsTest = [
  { id: 'methodName', label: 'Method' },
  { id: 'submittedDate', label: 'Requested', type: 'dateTime' },
  { id: 'receivedDate', label: 'Activated', type: 'dateTime' },
  { id: 'reportedDate', label: 'Completed', type: 'dateTime' },
  { id: 'status', label: 'Status' },
  { id: 'requesterEmail', label: 'Requester' },
  { id: 'sampleName', label: 'MIDAS Number', noWrap: true },
  { id: 'testSequenceNumber', label: 'TSN' },
  { id: 'age', label: 'Age of Sample' },
  { id: 'methodDescription', label: 'Method Description', width: '280px' },
  { id: 'submitterEmail', label: 'Submitter' },
  { id: 'analystEmail', label: 'Analyst' },
  { id: 'conditionOfferingName', label: 'Condition' },
  { id: 'testEstimate', label: 'Estimate' },
  { id: 'priority', label: 'Priority' },
  { id: 'testGroup', label: 'TestGroup' },
  { id: 'testFacilityFullName', label: 'Facility', width: '200px' },
  { id: 'externalLaboratoryName', label: 'External Lab Name', width: '200px' },  
  { id: 'billingInfo', label: 'Jade Number' },
  { id: 'testComment', label: 'Test Comments' }
];

const colsBacklog = [
  { id: 'methodName', label: 'Method' },
  { id: 'testFacilityFullName', label: 'Facility' },
  { id: 'conditionOfferingName', label: 'Condition' },
  { id: 'numberPending', label: 'Pending' },
  { id: 'numberBacklog', label: 'Backlog' },
  { id: 'numberInProgress', label: 'InProgress' }
];

const AnalyticalMetrics = ({ ...props }) => {
  const [searchFields, setSearchFields] = useState(defaultSearchFields);
  const [testFacilities, setTestFacilities] = useState(null);
  const [methods, setMethods] = useState([]);
  const [listTest, setListTest] = useState([]);
  const [listBacklog, setListBacklog] = useState([]);
  const [loadingTest, setLoadingTest] = useState(false);
  const [loadingBacklog, setLoadingBacklog] = useState(false);
  const [savedFilterInfo, setSavedFilterInfo] = useState(null);

  const [expanded, setExpanded] = useState(false);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const isMountedRef = React.useRef(true);
  const isTestsReqFieldsMissing = searchFields.tests && !(searchFields.startDate && searchFields.endDate);

  function loadFilter() {
    TestFacilityApi.getAll().then((result) => {
      if ((result !== undefined && result !== null)) {
        setTestFacilities(result.sort((a, b) => a.abbreviation.localeCompare(b.abbreviation)));
      }
    });
    MethodApi.getAll().then((res) => {
      if (res.length > 0) {
        setMethods(res.sort((a, b) => a.name.localeCompare(b.name)));
      }
    });
  }

  useEffect(() => {
    loadFilter();

    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    }
  }, [])

  useEffect(() => {
    if (testFacilities?.length > 0 && methods?.length > 0 && savedFilterInfo) {
      setSearchFields((props) => ({
          ...props,
          facility: null,
          method: null,
          tests: savedFilterInfo?.isTests ?? false,
          backlogs: savedFilterInfo?.isBacklogs ?? false,
          facilities: testFacilities?.filter(f => savedFilterInfo.facilitiesAbrs?.some(x => x === f.abbreviation)) ?? [],
          methods: savedFilterInfo?.methods ?? [],
        }))
    }
  }, [testFacilities, methods, savedFilterInfo]);

  function getFilterInfo(){
    return {
      isTests: searchFields?.tests ?? false,
      isBacklogs: searchFields?.backlogs ?? false,
      facilitiesAbrs: searchFields?.facilities?.map(f => f.abbreviation) ?? [],
      methods: searchFields?.methods ?? []
    }
  }

  async function handleSearch() {
    if (searchFields.tests) {
      setListTest([])
      setLoadingTest(true)
      getTest(200, 1);
    }
    if (searchFields.backlogs) {
      setListBacklog([])
      setLoadingBacklog(true)
      getBacklog();
    }
  }

  async function getTest(resultsPerPage, page, allData = []) {
    if (isMountedRef.current) {
      const response = await PiscesTest.get(
        searchFields.startDate.toISOString(),
        searchFields.endDate.toISOString(),
        searchFields.facilities?.map(f => f.abbreviation) ?? [],
        searchFields.methods,
        page,
        resultsPerPage)

      if (response) {
        allData = [...allData, ...response]
      } else {
        setListTest([]);
      }

      if (response === null || response.length === 0) {
        setLoadingTest(false)
        return allData;
      } else {
        page++;
        setListTest(allData)
        return getTest(resultsPerPage, page, allData);
      }
    }
  }

  async function getBacklog() {
    if (isMountedRef.current) {
      const response = await PiscesBacklog.get(
        searchFields.facilities?.map(f => f.abbreviation) ?? [],
        searchFields.methods)

      if (response === null || response.length === 0) {
        setListBacklog([]);
      } else {
        setListBacklog([...response])
      }      
      setLoadingBacklog(false)  
    }
  }

  const DownloadExcel = ({list, cols, fields, name}) => {
    return <IconButton onClick={e => {
      e.preventDefault();
      exportToExcel(list.map(item => {
        const object = {};
        cols.forEach((col, index) => {
          object[col] = fields[index].type === 'dateTime' ? convertDateFormat(item[fields[index].key]) : fields[index].key === 'sampleName' ? formatMidasNumber(item[fields[index].key]) : item[fields[index].key]
        })
        return object;
      }), name, false)
    }}>
      <FileDownloadIcon sx={{ m: 1 }} style={{ cursor: "pointer" }} />
    </IconButton>
  }

  const AccordionTable = (props) => {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('');

    const { name, colHeaders, loading, list = [] } = props;
    const fields = colHeaders.map(c => ({ key: c.id, type: c.type, width: c.width, noWrap: c.noWrap }));

    const handleChangePage = (event, newPage) => {
      setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
    };

    const handleRequestSort = (event, property) => {
      const isAsc = orderBy === property && order === 'asc';
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(property);
    };

    function stableSort(array, comparator) {
      const stabilizedThis = array.map((el, index) => [el, index]);
      stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
      });
      return stabilizedThis.map((el) => el[0]);
    }
  
    function getComparator(order, orderBy) {
      return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
    }
  
    function descendingComparator(a, b, orderBy) {
      if (b[orderBy] < a[orderBy]) {
      return -1;
      }
      if (b[orderBy] > a[orderBy]) {
          return 1;
      }
      return 0;
    }

    return (
      <Accordion expanded={expanded === name} onChange={handleChange(name)} onClick={(e) => {e.preventDefault();}}>
        <AccordionSummary
          className="pisces-accordion-sumamry"
          expandIcon={<>
            {expanded !== name && loading && <CircularProgress size={20} style={{ marginRight: "10px" }} />}
            <ExpandMoreIcon /></>}
          aria-controls="panel1a-content"
          id={`${name}-header`}
        >
          {/* <div> */}
            <Typography>{name}</Typography>
            {list.length > 0 && !loading && <DownloadExcel list={list} cols={colHeaders.map(c => c.label)} fields={fields} name={name} />}
          {/* </div> */}
        </AccordionSummary>
        <AccordionDetails>
          <UXDataTableWithoutBody
            tableWidth='100%'
            cols={colHeaders}
            blankFirstHeader={false}
            tableId="simpleResultTable"
            rowLength={list.length}
            enablePaging={true}
            rowsPerPage={rowsPerPage}
            page={page}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            noDataFoundMessage={'There is no data available'}
            enableAddIcon={false}
            addFunction={null}
            addToolTipText={null}
            enableSorteable={true}
            order={order}
            orderBy={orderBy}
            handleRequestSort={handleRequestSort}
            isDataLoading={loading}
          >
            <StyledTableBody key={"myTablebody"}>
              {stableSort(list, getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((item, rowIndex) => {
                
                  return (
                  [
                    <StyledTableRow
                      hover
                      key={`custom-row-${rowIndex}`}>
                      {fields.map(field => {
                        return <StyledTableCell
                          style={
                            { minWidth: field.width ?? '',
                              whiteSpace: field.noWrap ? 'nowrap' : '' 
                            }
                          }>
                            {field.type === 'dateTime' ? convertDateFormat(item[field.key]) :
                              field.key === 'sampleName' ? formatMidasNumber(item[field.key]) : 
                                item[field.key]
                              }
                          </StyledTableCell>
                        }
                      )}
                    </StyledTableRow>
                  ]
                );
              })}
            </StyledTableBody>
          </UXDataTableWithoutBody>
        </AccordionDetails>
      </Accordion>
    )
  }

  return (
    <div style={{ paddingBottom: '20px' }}>
      <h4>Analytical Metrics</h4>
      <div style={{marginTop: '20px', marginBottom: '10px', width: '700px'}}>
        <FilterManagement
          moduleName="reporting-analytical-metrics"
          getFilterInfo={getFilterInfo}
          applyFilterInfo={(fc) => setSavedFilterInfo(fc)}
        />
      </div>
      <StyledFormControl fullWidth>
        <StyledBox>
          <FormGroup>
            <FormControlLabel control={
              <Checkbox sx={{ '& .MuiSvgIcon-root': { fontSize: 28 } }} />} label="Tests"
              onClick={e => setSearchFields(() => ({
                ...searchFields,
                tests: !searchFields.tests
              }))}
              checked={searchFields.tests}
            />
          </FormGroup>
          <FormGroup>
            <FormControlLabel control={
              <Checkbox sx={{ '& .MuiSvgIcon-root': { fontSize: 28 } }} />} label="Backlogs"
              onClick={e => setSearchFields(() => ({
                ...searchFields,
                backlogs: !searchFields.backlogs
              }))}
              checked={searchFields.backlogs}
            />
          </FormGroup>
        </StyledBox>
        <StyledBox sx={{ display: searchFields.tests ? 'flex' : 'none' }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DesktopDatePicker
              size="small"
              inputFormat="MM/dd/yyyy"
              label="Start Date"
              onChange={e => setSearchFields(() => ({
                ...searchFields,
                startDate: e
              }))}
              maxDate={searchFields.endDate}
              value={searchFields.startDate}
              renderInput={(params) => <StyledShortTextField onKeyDown={DatePickerKeyDownEvent} {...params} margin="normal" variant="outlined" size="small" InputLabelProps={{ shrink: true }} inputProps={{ ...params.inputProps }} InputProps={{ ...params.InputProps }} />}
            />
          </LocalizationProvider>

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DesktopDatePicker
              size="small"
              inputFormat="MM/dd/yyyy"
              label="End Date"
              onChange={e => setSearchFields(() => ({
                ...searchFields,
                endDate: e
              }))}
              minDate={searchFields.startDate}
              maxDate={new Date()}
              value={searchFields.endDate}
              renderInput={(params) => <StyledShortTextField onKeyDown={DatePickerKeyDownEvent} {...params} margin="normal" variant="outlined" size="small" InputLabelProps={{ shrink: true }} inputProps={{ ...params.inputProps }} InputProps={{ ...params.InputProps }} />}
            />
          </LocalizationProvider>
        </StyledBox>
        <StyledBox>
          <StyledAutocomplete
            size="small"
            id="facility-select"
            disablePortal
            noOptionsText={"Loading Facilities..."}
            options={testFacilities !== null ?
              testFacilities
                .filter(f => !searchFields.facilities?.some(s => s?.abbreviation === f?.abbreviation)) : []}
            getOptionLabel={(option) => option.testFacilityFullName}
            onChange={(e, value) => {
              value = value === null ? "" : value
              setSearchFields(() => ({
                ...searchFields,
                facility: value,
              }))
            }}
            autoHighlight
            autoSelect
            value={searchFields.facility === '' ? null : searchFields.facility}
            renderInput={(params) => <TextField {...params} margin="normal" variant="outlined" size="small" label="Facility" InputProps={{ ...params.InputProps }} />}
          />
          <GlobalButton
            style={{ marginTop: '8px', width: "12rem" }}
            variant='contained'
            onClick={() => {
              setSearchFields(() => ({
                ...searchFields,
                facilities: [...searchFields.facilities.filter(f => f?.abbreviation !== searchFields.facility?.abbreviation), searchFields.facility],
                facility: null
              }))
            }}
            disabled={!searchFields.facility}
          >Add Facility</GlobalButton>
          <StyledAutocomplete
            size="small"
            id="facilities-select"
            multiple
            limitTags={10}
            disableCloseOnSelect
            noOptionsText={"No Options"}
            options={searchFields.facilities}
            getOptionLabel={(option) => option.testFacilityFullName}
            onChange={(event, newValue) => {
              setSearchFields(() => ({
                ...searchFields,
                facilities: newValue
              }))
            }}
            value={searchFields.facilities}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                {option}
              </li>
            )}
            style={{ width: 600 }}
            autoHighlight
            freeSolo
            renderInput={(params) => <TextField
              rows={1}
              multiline
              {...params}
              margin="normal"
              variant="outlined"
              size="small"
              label="Facilities to Search"
              inputProps={{ ...params.inputProps, readOnly: true }}
            />}
          />
        </StyledBox>
        <StyledBox>
          <StyledAutocomplete
            size="small"
            id="method-select"
            disablePortal
            noOptionsText={"Loading Methods..."}
            options={methods !== null ?
              methods
                .map((method) => method.name)
                .filter(f => !searchFields.methods?.some(s => s === f)) : []}
            getOptionLabel={(option) => option}
            onChange={(e, value) => {
              value = value === null ? "" : value
              setSearchFields(() => ({
                ...searchFields,
                method: value,
              }))
            }}
            autoHighlight
            autoSelect
            value={searchFields.method === '' ? null : searchFields.method}
            renderInput={(params) => <TextField {...params} margin="normal" variant="outlined" size="small" label="Method" InputProps={{ ...params.InputProps }} />}
          />
          <GlobalButton
            style={{ marginTop: '8px', width: "12rem" }}
            variant='contained'
            onClick={() => {
              setSearchFields(() => ({
                ...searchFields,
                methods: [...searchFields.methods.filter(f => f !== searchFields.method), searchFields.method],
                method: null
              }))
            }}
            disabled={!searchFields.method}
          >Add Method</GlobalButton>
          <StyledAutocomplete
            size="small"
            id="methods-select"
            multiple
            limitTags={10}
            disableCloseOnSelect
            noOptionsText={"No Options"}
            options={searchFields.methods}
            getOptionLabel={(option) => option}
            onChange={(event, newValue) => {
              setSearchFields(() => ({
                ...searchFields,
                methods: newValue
              }))
            }}
            value={searchFields.methods}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                {option}
              </li>
            )}
            style={{ width: 600 }}
            autoHighlight
            freeSolo
            renderInput={(params) => <TextField
              rows={1}
              multiline
              {...params}
              margin="normal"
              variant="outlined"
              size="small"
              label="Methods to Search"
              inputProps={{ ...params.inputProps, readOnly: true }}
            />}
          />
          <GlobalButton
            style={{ marginTop: '8px', width: "12rem" }}
            variant='contained'
            onClick={handleSearch}
            disabled={!(searchFields.tests || searchFields.backlogs) || isTestsReqFieldsMissing || loadingTest || loadingBacklog}
          >Get Metrics</GlobalButton>
          {(loadingTest || loadingBacklog) && <CircularProgress
              style={{textTransform: "none", marginLeft:"15px"}}>
            </CircularProgress>
          }
        </StyledBox>
      </StyledFormControl>
      <div style={{marginTop: 20}}>
        {searchFields.tests && <AccordionTable
          name={'Tests'}
          colHeaders={colsTest}
          list={listTest}
          loading={loadingTest}
        />}
        {searchFields.backlogs && <AccordionTable
          name={'Backlog'}
          colHeaders={colsBacklog}
          list={listBacklog}
          loading={loadingBacklog}
        />}
      </div>
    </div>
  );
};

export default AnalyticalMetrics; 
